import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ShipmentDetails from '../components/ShipmentDetails';
import './PackingListManager.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function PackingListManager({ token }) {
  const navigate = useNavigate();
  
  const [packingLists, setPackingLists] = useState([]);
  const [shipmentDetails, setShipmentDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (token) {
      fetchPackingLists();
    }
  }, [statusFilter, token]);

  const fetchPackingLists = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/packing-lists`;
      if (statusFilter) {
        url += `?status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        setError('Failed to load packing lists');
        return;
      }
      
      const data = await response.json();
      setPackingLists(Array.isArray(data) ? data : []);
      
      // Fetch shipment details for each packing list
      const shipmentData = {};
      for (const pl of data) {
        try {
          const shipmentRes = await fetch(`${API_URL}/packing-lists/${pl.packing_list_id}/shipment`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (shipmentRes.ok) {
            shipmentData[pl.packing_list_id] = await shipmentRes.json();
          }
        } catch (err) {
          console.error('Error fetching shipment:', err);
        }
      }
      setShipmentDetails(shipmentData);
    } catch (err) {
      setError('Error loading packing lists: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (packing_list) => {
    setEditingId(packing_list.packing_list_id);
    setEditForm({
      order_number: packing_list.order_number || '',
      invoice_number: packing_list.invoice_number || '',
      client_po_number: packing_list.client_po_number || '',
      notes: packing_list.notes || ''
    });
    setError('');
    setSuccess('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/packing-lists/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to update packing list');
        return;
      }

      setSuccess('Packing list updated successfully');
      setEditingId(null);
      setTimeout(() => fetchPackingLists(), 1000);
    } catch (err) {
      setError('Error updating packing list: ' + err.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (packing_list_id) => {
    if (!window.confirm('Are you sure you want to delete this packing list?')) return;

    try {
      const response = await fetch(`${API_URL}/packing-lists/${packing_list_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to delete packing list');
        return;
      }

      setSuccess('Packing list deleted successfully');
      setTimeout(() => fetchPackingLists(), 1000);
    } catch (err) {
      setError('Error deleting packing list: ' + err.message);
    }
  };

  const toggleExpanded = (packing_list_id) => {
    setExpandedId(expandedId === packing_list_id ? null : packing_list_id);
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'pending_data': 'badge-warning',
      'complete': 'badge-success',
      'delivered': 'badge-info'
    };
    return classes[status] || 'badge-secondary';
  };

  if (loading) return <div className="loading">Loading packing lists...</div>;

  return (
    <div className="packing-list-manager-container">
      <div className="manager-header">
        <h1>Packing Lists</h1>
        <p>View and manage all packing lists</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="filters">
        <label htmlFor="statusFilter">Filter by Status:</label>
        <select
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="pending_data">Pending Data</option>
          <option value="complete">Complete</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>

      {packingLists.length === 0 ? (
        <div className="no-data">No packing lists found</div>
      ) : (
        <div className="packing-lists-grid">
          {packingLists.map(pl => (
            <div key={pl.packing_list_id} className="packing-list-card">
              <div className="card-header">
                <div className="header-content">
                  <h3>
                    {pl.order_number || pl.invoice_number || `PL-${pl.packing_list_id}`}
                  </h3>
                  <span className={`badge ${getStatusBadgeClass(pl.status)}`}>
                    {pl.status}
                  </span>
                </div>
                <button 
                  className="expand-btn"
                  onClick={() => toggleExpanded(pl.packing_list_id)}
                >
                  {expandedId === pl.packing_list_id ? '▼' : '▶'}
                </button>
              </div>

              <div className="card-content">
                <div className="quick-info">
                  {pl.supplier && (
                    <div className="info-item">
                      <span className="label">Supplier:</span>
                      <span className="value">{pl.supplier.supplier_name}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="label">Invoice:</span>
                    <span className="value">{pl.invoice_number || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Upload Date:</span>
                    <span className="value">{new Date(pl.upload_date).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Expanded Section */}
                {expandedId === pl.packing_list_id && (
                  <div className="expanded-section">
                    {/* Shipment Details Block */}
                    {shipmentDetails[pl.packing_list_id] && (
                      <ShipmentDetails 
                        shipment={shipmentDetails[pl.packing_list_id]}
                        packing_list={pl}
                        loading={false}
                      />
                    )}

                    {/* Edit Form */}
                    {editingId === pl.packing_list_id ? (
                      <div className="edit-form-section">
                        <h4>Edit Details</h4>
                        <div className="form-group">
                          <label>Order Number</label>
                          <input
                            type="text"
                            name="order_number"
                            value={editForm.order_number}
                            onChange={handleEditChange}
                            placeholder="Order Number"
                          />
                        </div>
                        <div className="form-group">
                          <label>Invoice Number</label>
                          <input
                            type="text"
                            name="invoice_number"
                            value={editForm.invoice_number}
                            onChange={handleEditChange}
                            placeholder="Invoice Number"
                          />
                        </div>
                        <div className="form-group">
                          <label>Client PO Number</label>
                          <input
                            type="text"
                            name="client_po_number"
                            value={editForm.client_po_number}
                            onChange={handleEditChange}
                            placeholder="Client PO Number"
                          />
                        </div>
                        <div className="form-group">
                          <label>Notes</label>
                          <textarea
                            name="notes"
                            value={editForm.notes}
                            onChange={handleEditChange}
                            placeholder="Notes"
                            rows="3"
                          />
                        </div>
                        <div className="form-buttons">
                          <button className="btn btn-primary" onClick={handleSaveEdit}>
                            Save
                          </button>
                          <button className="btn btn-secondary" onClick={handleCancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="details-section">
                        <div className="detail-row">
                          <span className="label">Order Number:</span>
                          <span className="value">{pl.order_number || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Invoice Number:</span>
                          <span className="value">{pl.invoice_number || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Client PO:</span>
                          <span className="value">{pl.client_po_number || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Notes:</span>
                          <span className="value">{pl.notes || 'None'}</span>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="action-buttons">
                      {editingId !== pl.packing_list_id && (
                        <>
                          <button 
                            className="btn btn-secondary"
                            onClick={() => navigate(`/packing-list/${pl.packing_list_id}/cargo`)}
                          >
                            Add/Edit Cargo
                          </button>
                          <button 
                            className="btn btn-primary"
                            onClick={() => handleEdit(pl)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger"
                            onClick={() => handleDelete(pl.packing_list_id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}